/**
 * Parent-side AI rewrite modal for selected text.
 */

import { getAuthHeaders } from '../templates/template-api.js';
import { escapeHTML } from '../shared/html-escape.js';
import { createModal, createConfirmDialog } from '../ui/components.js';

let modalOverlay = null;
let closeKitModal = null;
let activeRunState = null;

const GOAL_OPTIONS = [
  { key: 'friendly', label: 'Friendly' },
  { key: 'professional', label: 'Professional' },
  { key: 'shorter', label: 'Shorter' },
  { key: 'scannable', label: 'Scannable' }
];

function textToHTML(text) {
  return escapeHTML(text || '')
    .replace(/\r\n?/g, '\n')
    .replace(/\n/g, '<br>');
}

function replaceTextNodeWithBreaks(node, value, doc) {
  const parts = String(value || '')
    .replace(/\r\n?/g, '\n')
    .split('\n');
  if (parts.length === 1) {
    node.nodeValue = parts[0];
    return;
  }
  const fragment = doc.createDocumentFragment();
  parts.forEach((part, index) => {
    if (index) fragment.appendChild(doc.createElement('br'));
    if (part) fragment.appendChild(doc.createTextNode(part));
  });
  node.replaceWith(fragment);
}

/** Preserve the selection's inline element structure while replacing its text. */
export function preserveInlineFormattingHTML(originalHtml, originalText, nextText, doc = document) {
  const sourceHtml = String(originalHtml || '');
  const rewritten = String(nextText || '');
  if (!sourceHtml || !/<[a-z][\s\S]*>/i.test(sourceHtml)) return textToHTML(rewritten);

  const template = doc.createElement('template');
  template.innerHTML = sourceHtml;
  const NodeFilterCtor = doc.defaultView?.NodeFilter || globalThis.NodeFilter;
  const walker = doc.createTreeWalker(template.content, NodeFilterCtor.SHOW_TEXT);
  const textNodes = [];
  let node;
  while ((node = walker.nextNode())) textNodes.push(node);
  if (!textNodes.length) return textToHTML(rewritten);

  const sourceLength = Math.max(
    1,
    textNodes.reduce((sum, textNode) => sum + textNode.nodeValue.length, 0)
  );
  let consumed = 0;
  let sourceConsumed = 0;
  for (let index = 0; index < textNodes.length; index += 1) {
    const textNode = textNodes[index];
    sourceConsumed += textNode.nodeValue.length;
    const end =
      index === textNodes.length - 1
        ? rewritten.length
        : Math.max(
            consumed,
            Math.min(
              rewritten.length,
              Math.round((sourceConsumed / sourceLength) * rewritten.length)
            )
          );
    const slice = rewritten.slice(consumed, end);
    consumed = end;
    replaceTextNodeWithBreaks(textNode, slice, doc);
  }

  return template.innerHTML;
}

function splitSentences(text) {
  const input = String(text || '').trim();
  if (!input) return [];
  return input
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);
}

function buildSentenceRows(beforeText, afterText) {
  const before = splitSentences(beforeText);
  const after = splitSentences(afterText);
  const size = Math.max(before.length, after.length);
  const rows = [];
  for (let i = 0; i < size; i++) {
    const original = before[i] || '';
    const rewritten = after[i] || original;
    const changed = original !== rewritten;
    rows.push({
      index: i,
      original,
      rewritten,
      changed,
      selected: changed
    });
  }
  return rows;
}

function getOrCreateSessionId() {
  const key = 'pb-ai-rewrite-session-id';
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const next = crypto.randomUUID();
    localStorage.setItem(key, next);
    return next;
  } catch {
    return crypto.randomUUID();
  }
}

function closeModal() {
  // Clear the handle BEFORE invoking it: the kit's close() fires onClose, which
  // routes back here, and this ordering makes that re-entry a no-op instead of
  // a loop.
  const close = closeKitModal;
  closeKitModal = null;
  modalOverlay = null;
  activeRunState = null;
  close?.();
}

function getCheckedGoals(container) {
  return Array.from(container.querySelectorAll('input[name="pb-ai-goal"]:checked'))
    .map((input) => String(input.value || '').trim())
    .filter(Boolean);
}

function setBusy(container, busy, label = '') {
  const runBtn = container.querySelector('#pb-ai-rewrite-run');
  const applyBtn = container.querySelector('#pb-ai-rewrite-apply');
  const applyAllBtn = container.querySelector('#pb-ai-rewrite-apply-all');
  if (runBtn) {
    runBtn.disabled = busy;
    runBtn.textContent = busy ? label || 'Rewriting…' : 'Rewrite';
  }
  if (applyBtn) applyBtn.disabled = busy;
  if (applyAllBtn) applyAllBtn.disabled = busy;
}

function renderSentenceRows(container, rows) {
  const root = container.querySelector('#pb-ai-rewrite-diff');
  if (!root) return;
  if (!rows || !rows.length) {
    root.innerHTML = '<div class="pb-ai-rewrite-empty">No sentence-level changes detected.</div>';
    return;
  }
  root.innerHTML = rows
    .map(
      (row) => `
    <label class="pb-ai-rewrite-row${row.changed ? '' : ' pb-ai-rewrite-row-unchanged'}">
      <input type="checkbox" class="pb-checkbox-input" data-row-index="${row.index}" ${row.selected ? 'checked' : ''} ${row.changed ? '' : 'disabled'} />
      <div class="pb-ai-rewrite-row-body">
        <div class="pb-ai-rewrite-row-before">${escapeHTML(row.original || '(empty)')}</div>
        <div class="pb-ai-rewrite-row-after">${escapeHTML(row.rewritten || '(empty)')}</div>
      </div>
    </label>
  `
    )
    .join('');
}

function collectAppliedText(rows) {
  return rows
    .map((row) => (row.changed && row.selected ? row.rewritten : row.original))
    .filter((line) => typeof line === 'string')
    .join(' ')
    .trim();
}

function writeStatus(container, message, kind = 'info') {
  const status = container.querySelector('#pb-ai-rewrite-status');
  if (!status) return;
  status.className = `pb-ai-rewrite-status pb-ai-rewrite-status-${kind}`;
  status.textContent = message || '';
}

async function callRewriteEndpoint(payload, options = {}) {
  const response = await fetch('/api/ai/rewrite', {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload)
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(String(data.error || `Rewrite failed (${response.status})`));
    error.status = response.status;
    error.code = data.code || 'rewrite_failed';
    error.retryAfter = data.retryAfter || null;
    error.payload = data;
    error.continueAfterSoftCap = !!options.continueAfterSoftCap;
    throw error;
  }
  return data;
}

/**
 * Open AI rewrite modal for the active selection.
 */
export async function openAIRewriteModal(options) {
  const { requestSelectionSnapshot, replaceSelectionHTML, getRequestContext, onApplied } =
    options || {};

  if (
    typeof requestSelectionSnapshot !== 'function' ||
    typeof replaceSelectionHTML !== 'function'
  ) {
    throw new Error('openAIRewriteModal requires selection snapshot and replacement callbacks');
  }

  const snapshot = await requestSelectionSnapshot();
  const selectionText = String(snapshot?.text || '').trim();
  if (!snapshot?.hasSelection || !selectionText) {
    return { opened: false, reason: 'no-selection' };
  }
  const selectionContext = {
    originalHtml: String(snapshot.html || ''),
    originalText: String(snapshot.text || ''),
    rangeSnapshot: snapshot.rangeSnapshot || null
  };

  closeModal();
  // Was a hand-rolled overlay carrying role="dialog" aria-modal="true" with no
  // focus trap, no Escape, no focus restore and no stacking. The kit owns all
  // four; the markup below is the body/footer content only.
  const kit = createModal({
    title: 'AI Rewrite',
    size: 'lg',
    overlayClassName: 'pb-ai-rewrite-overlay',
    className: 'pb-ai-rewrite-modal',
    initialFocus: () => {
      const input = modalOverlay?.querySelector('#pb-ai-rewrite-original');
      input?.select();
      return input;
    },
    onClose: () => {
      closeKitModal = null;
      modalOverlay = null;
      activeRunState = null;
    }
  });
  modalOverlay = kit.overlay;
  closeKitModal = kit.close;
  kit.body.classList.add('pb-ai-rewrite-body');
  kit.body.innerHTML = `
        <div class="pb-ai-rewrite-section">
          <div class="pb-ai-rewrite-label">Goals</div>
          <div class="pb-ai-rewrite-goals">
            ${GOAL_OPTIONS.map(
              (goal) => `
              <label class="pb-ai-rewrite-goal">
                <input type="checkbox" class="pb-checkbox-input" name="pb-ai-goal" value="${goal.key}" ${goal.key === 'shorter' ? 'checked' : ''}/>
                <span>${escapeHTML(goal.label)}</span>
              </label>
            `
            ).join('')}
          </div>
        </div>
        <div class="pb-ai-rewrite-section">
          <div class="pb-ai-rewrite-label">Selected Text</div>
          <textarea id="pb-ai-rewrite-original" class="pb-ai-rewrite-textarea" rows="5">${escapeHTML(selectionText)}</textarea>
        </div>
        <div class="pb-ai-rewrite-actions">
          <button type="button" class="pb-btn pb-btn-sm" id="pb-ai-rewrite-run">Rewrite</button>
          <span id="pb-ai-rewrite-status" class="pb-ai-rewrite-status pb-ai-rewrite-status-info"></span>
        </div>
        <div class="pb-ai-rewrite-section">
          <div class="pb-ai-rewrite-label">Rewrite Preview</div>
          <textarea id="pb-ai-rewrite-result" class="pb-ai-rewrite-textarea" rows="5" placeholder="Run rewrite to preview output."></textarea>
        </div>
        <div class="pb-ai-rewrite-section">
          <div class="pb-ai-rewrite-label">Sentence Changes</div>
          <div id="pb-ai-rewrite-diff" class="pb-ai-rewrite-diff"></div>
        </div>
  `;
  kit.footer.innerHTML = `
    <button type="button" class="pb-btn pb-btn-outline" id="pb-ai-rewrite-cancel">Cancel</button>
    <button type="button" class="pb-btn pb-btn-outline" id="pb-ai-rewrite-apply">Apply Selected</button>
    <button type="button" class="pb-btn pb-btn-primary" id="pb-ai-rewrite-apply-all">Apply All</button>
  `;

  const cancelBtn = modalOverlay.querySelector('#pb-ai-rewrite-cancel');
  const runBtn = modalOverlay.querySelector('#pb-ai-rewrite-run');
  const applyBtn = modalOverlay.querySelector('#pb-ai-rewrite-apply');
  const applyAllBtn = modalOverlay.querySelector('#pb-ai-rewrite-apply-all');
  const originalInput = modalOverlay.querySelector('#pb-ai-rewrite-original');
  const resultInput = modalOverlay.querySelector('#pb-ai-rewrite-result');
  const diffRoot = modalOverlay.querySelector('#pb-ai-rewrite-diff');

  // The kit owns the header close button, the backdrop click and Escape.
  cancelBtn?.addEventListener('click', () => closeModal());

  const bindRowsFromDOM = () => {
    if (!activeRunState?.rows || !diffRoot) return;
    for (const checkbox of diffRoot.querySelectorAll('input[type="checkbox"][data-row-index]')) {
      const index = Number.parseInt(checkbox.getAttribute('data-row-index'), 10);
      if (!Number.isInteger(index)) continue;
      const row = activeRunState.rows[index];
      if (!row) continue;
      row.selected = !!checkbox.checked;
    }
  };

  runBtn?.addEventListener('click', async () => {
    const currentText = String(originalInput?.value || '').trim();
    if (!currentText) {
      writeStatus(modalOverlay, 'Select text before running AI rewrite.', 'error');
      return;
    }
    const requestContext = typeof getRequestContext === 'function' ? getRequestContext() : {};
    const payloadBase = {
      text: currentText,
      goals: getCheckedGoals(modalOverlay),
      sessionId: requestContext?.sessionId || getOrCreateSessionId(),
      brandVoice: requestContext?.brandVoice || null,
      aiLogPrompts: requestContext?.flags?.aiLogPrompts !== false,
      aiRewriteEnabled: requestContext?.flags?.aiRewrite !== false,
      clientInfo: requestContext?.clientInfo || null,
      projectName: requestContext?.projectName || 'Untitled'
    };

    setBusy(modalOverlay, true, 'Rewriting…');
    writeStatus(modalOverlay, 'Running rewrite…', 'info');

    try {
      const data = await callRewriteEndpoint(payloadBase);
      const rewrittenText = String(data?.rewrittenText || '').trim();
      if (!rewrittenText) {
        writeStatus(modalOverlay, 'Rewrite returned empty output. Try again.', 'error');
        return;
      }
      if (resultInput) resultInput.value = rewrittenText;
      activeRunState = {
        originalText: currentText,
        rewrittenText,
        ...selectionContext,
        rows: buildSentenceRows(currentText, rewrittenText)
      };
      renderSentenceRows(modalOverlay, activeRunState.rows);
      const warningMessage =
        Array.isArray(data?.warnings) && data.warnings.length ? ` ${data.warnings.join(' ')}` : '';
      writeStatus(modalOverlay, `Rewrite complete.${warningMessage}`, 'success');
    } catch (error) {
      if (error?.code === 'session_soft_cap') {
        const proceed = await createConfirmDialog({
          title: 'Rewrite spend cap reached',
          message: 'Session rewrite soft cap reached. Continue with additional spend?',
          confirmLabel: 'Continue',
          cancelLabel: 'Stop here'
        });
        // The dialog is async, so the rewrite modal can be gone by the time it
        // resolves — every write below targets modalOverlay.
        if (proceed && modalOverlay) {
          try {
            const data = await callRewriteEndpoint(
              { ...payloadBase, continueAfterSoftCap: true },
              { continueAfterSoftCap: true }
            );
            const rewrittenText = String(data?.rewrittenText || '').trim();
            if (!rewrittenText) {
              writeStatus(modalOverlay, 'Rewrite returned empty output. Try again.', 'error');
              return;
            }
            if (resultInput) resultInput.value = rewrittenText;
            activeRunState = {
              originalText: currentText,
              rewrittenText,
              ...selectionContext,
              rows: buildSentenceRows(currentText, rewrittenText)
            };
            renderSentenceRows(modalOverlay, activeRunState.rows);
            writeStatus(modalOverlay, 'Rewrite complete.', 'success');
          } catch (retryError) {
            writeStatus(modalOverlay, retryError?.message || 'Rewrite failed.', 'error');
          }
        } else {
          writeStatus(modalOverlay, 'Rewrite cancelled at soft cap.', 'info');
        }
      } else {
        writeStatus(modalOverlay, error?.message || 'Rewrite failed.', 'error');
      }
    } finally {
      setBusy(modalOverlay, false);
    }
  });

  const applyFromRows = async (applyAll = false) => {
    if (!activeRunState) return;
    bindRowsFromDOM();
    if (applyAll) {
      for (const row of activeRunState.rows) {
        if (row.changed) row.selected = true;
      }
    }
    const nextText = collectAppliedText(activeRunState.rows);
    if (!nextText) {
      writeStatus(modalOverlay, 'No rewritten text selected to apply.', 'error');
      return;
    }
    setBusy(modalOverlay, true, 'Applying…');
    try {
      const replacementHtml = preserveInlineFormattingHTML(
        activeRunState.originalHtml,
        activeRunState.originalText,
        nextText
      );
      const ok = await replaceSelectionHTML(replacementHtml, activeRunState.rangeSnapshot);
      if (!ok) {
        writeStatus(modalOverlay, 'Could not apply rewrite to the active selection.', 'error');
        return;
      }
      if (typeof onApplied === 'function') onApplied({ text: nextText });
      closeModal();
    } catch (error) {
      writeStatus(modalOverlay, error?.message || 'Failed to apply rewrite.', 'error');
    } finally {
      if (modalOverlay) setBusy(modalOverlay, false);
    }
  };

  applyBtn?.addEventListener('click', () => {
    applyFromRows(false);
  });
  applyAllBtn?.addEventListener('click', () => {
    applyFromRows(true);
  });

  writeStatus(modalOverlay, 'Choose goals, then run rewrite.', 'info');

  return { opened: true };
}

export function closeAIRewriteModal() {
  closeModal();
}

export function isAIRewriteModalOpen() {
  return !!modalOverlay;
}
